import { useQuery } from "@tanstack/react-query";
import { projectionService } from "@/core/api/services";
import { workbenchKeys } from "@/core/api/queryKeys";
import { useAuth } from "@hub/auth";
import { parseProjectionData } from "./projectionUtils";

export interface WorkerStats {
  workerId: string;
  workerName: string;
  itemsCompleted: number;
  avgHandleTimeMinutes: number;
  activeTimeMinutes: number;
  utilization: number;
}

export function useWorkerProductivity(teamId: string, dateRange: string) {
  const { user } = useAuth();
  const isManager = user?.role === "admin" || user?.role === "operator";

  return useQuery({
    queryKey: workbenchKeys.dashboard.workers(teamId, dateRange),
    queryFn: async (): Promise<WorkerStats[]> => {
      const response = await projectionService.query({
        type: "workqueue_worker_daily",
        filters: { team: teamId, date_range: dateRange },
        orderBy: "items_completed",
        descending: true,
        limit: 100,
        offset: 0,
      });

      return (response.projections ?? []).map((p) => {
        const d = parseProjectionData(p.data);
        return {
          workerId: (d.worker_id as string) ?? "",
          workerName: (d.worker_name as string) ?? (d.worker_id as string) ?? "Unknown",
          itemsCompleted: (d.items_completed as number) ?? 0,
          avgHandleTimeMinutes: (d.avg_handle_time_minutes as number) ?? 0,
          activeTimeMinutes: (d.active_time_minutes as number) ?? 0,
          utilization: (d.utilization as number) ?? 0,
        };
      });
    },
    enabled: !!teamId && isManager,
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
  });
}
